import {
  isOptionSource,
  type BaseListViewDef,
  type PendingListViewDef,
} from "@/base/list/types";

// Declares a BaseListView definition and checks it on the spot (BRK-S211), so a
// list that can't render well fails in tests, not on a broker's screen: a table
// wider than six columns doesn't fit at md, PMS serves at most 100 rows a page
// (`lib/list.ts`), and every row needs exactly one link.

export const MAX_LIST_COLUMNS = 6;
/** A `wide` list (BRK-S501): a wireframe table that names more columns than
 *  fit at md. It shows cards until lg and scrolls inside its own frame. */
export const MAX_WIDE_LIST_COLUMNS = 9;
export const MAX_PAGE_SIZE = 100;

export function defineListView<Row>(
  def: BaseListViewDef<Row>,
): BaseListViewDef<Row> {
  const where = `The ${def.noun} list`;
  // Row actions take a trailing cell of their own, so they count.
  const max = def.wide ? MAX_WIDE_LIST_COLUMNS : MAX_LIST_COLUMNS;
  if (def.columns.length + (def.rowActions ? 1 : 0) > max) {
    throw new Error(`${where} has at most ${max} columns.`);
  }
  if (def.columns.filter((c) => c.kind === "title").length !== 1) {
    throw new Error(`${where} needs exactly one title column: the row link.`);
  }
  if (
    !Number.isInteger(def.pageSize) ||
    def.pageSize < 1 ||
    def.pageSize > MAX_PAGE_SIZE
  ) {
    throw new Error(`${where} has a page size of 1 to ${MAX_PAGE_SIZE}.`);
  }
  if (
    def.sort &&
    !def.sort.options.some((o) => o.value === def.sort?.default.field)
  ) {
    throw new Error(`${where}'s sort default must be one of its options.`);
  }
  return def;
}

/** Declares a list PMS can't serve yet (BRK-S251), held to the same column
 *  limit, so the columns it promises fit when it goes live. */
export function definePendingListView(
  def: PendingListViewDef,
): PendingListViewDef {
  const where = `The ${def.noun} list`;
  if (def.columns.length < 1) {
    throw new Error(`${where} shows at least one column.`);
  }
  if (def.columns.length > MAX_LIST_COLUMNS) {
    throw new Error(`${where} has at most ${MAX_LIST_COLUMNS} columns.`);
  }
  return def;
}

/** Every query parameter a definition can send, paging included: what the
 *  contract test holds against the pinned GET. */
export function listApiParams<Row>(def: BaseListViewDef<Row>): string[] {
  return [
    ...(def.search ? [def.search.apiParam] : []),
    ...(def.toggles ?? []).map((t) => t.apiParam),
    ...(def.filters ?? []).map((f) => f.apiParam),
    ...(def.linkParams ?? []).map((l) => l.apiParam),
    ...(def.sort ? [def.sort.field.apiParam, def.sort.dir.apiParam] : []),
    "limit",
    "offset",
  ];
}

/** The GETs a definition's filters read their options from (BRK-S254), held
 *  to the pinned contract like its query parameters. */
export function listOptionEndpoints<Row>(def: BaseListViewDef<Row>): string[] {
  return (def.filters ?? []).flatMap((f) =>
    isOptionSource(f.options) ? [f.options.endpoint] : [],
  );
}
